import type {
  MeshConnectivity,
  MeshUserSyncEntry,
  MeshUserSyncReport,
  MeshUserSyncStatus,
  ProcessSnapshot,
  TargetStatus,
  TenantMeshStatus,
} from '@/types/control/mesh-connectivity';

import { controlApiRequest } from './control-api-client';

/**
 * Control's mesh connectivity surfaces.
 *
 * Two things to know before reading further.
 *
 * `GET /mesh-connectivity` **always answers 200**, even when its store is not
 * configured — that case arrives as both error strings set rather than as a
 * failure status. So there is no error path to catch; the shape has to be read.
 *
 * `GET /tenants/mesh-status` **fans out a live mesh probe per tenant**, five at
 * a time. It is the most expensive call on the admin surface, which is why its
 * hook refuses to refetch on mount or focus.
 */

type TargetResponse = {
  ok?: unknown;
  last_ok_at?: unknown;
  skipped?: unknown;
  skip_reason?: unknown;
  last_error?: unknown;
  http_status?: unknown;
};

type SnapshotResponse = {
  updated_at?: unknown;
  mesh_core?: unknown;
  mesh_stream?: unknown;
  process_role?: unknown;
};

type ConnectivityResponse = {
  api?: unknown;
  api_error?: unknown;
  worker?: unknown;
  worker_error?: unknown;
};

function asString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

/** Optional strings are `omitempty` on the wire — absent rather than null. */
function asOptional(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null;
}

function asNumber(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

function toTarget(value: unknown): TargetStatus {
  const res = (value ?? {}) as TargetResponse;

  return {
    ok: res.ok === true,
    lastOkAt: asOptional(res.last_ok_at),
    skipped: res.skipped === true,
    skipReason: asOptional(res.skip_reason),
    lastError: asOptional(res.last_error),
    // omitempty drops a zero, and a zero would mean "no response at all"
    // anyway, so both read as "no status to show".
    httpStatus: asNumber(res.http_status) || null,
  };
}

function toSnapshot(value: unknown): ProcessSnapshot | null {
  if (typeof value !== 'object' || value === null) {
    return null;
  }

  const res = value as SnapshotResponse;

  return {
    updatedAt: asString(res.updated_at),
    // Value types on the wire, so always present.
    meshCore: toTarget(res.mesh_core),
    meshStream: toTarget(res.mesh_stream),
    processRole: asString(res.process_role),
  };
}

const SYNC_STATUSES: MeshUserSyncStatus[] = [
  'in_sync',
  'missing',
  'ineligible',
];

function toSyncStatus(value: unknown): MeshUserSyncStatus {
  const raw = asString(value) as MeshUserSyncStatus;
  // Narrowed rather than trusted: the field is a plain string on the wire.
  return SYNC_STATUSES.includes(raw) ? raw : 'ineligible';
}

function toSyncEntry(value: unknown): MeshUserSyncEntry {
  const res = (value ?? {}) as Record<string, unknown>;

  return {
    userId: asString(res.user_id),
    email: asString(res.email),
    name: asOptional(res.name),
    meshUserId: asOptional(res.mesh_user_id),
    accessLevel: asString(res.access_level),
    eligible: res.eligible === true,
    present: res.present === true,
    status: toSyncStatus(res.status),
    reason: asOptional(res.reason),
  };
}

function toSyncReport(value: unknown): MeshUserSyncReport {
  const res = (value ?? {}) as Record<string, unknown>;

  return {
    tenantId: asString(res.tenant_id),
    meshConfigured: res.mesh_configured === true,
    totalUsers: asNumber(res.total_users),
    expected: asNumber(res.expected),
    present: asNumber(res.present),
    missing: asNumber(res.missing),
    ineligible: asNumber(res.ineligible),
    users: Array.isArray(res.users) ? res.users.map(toSyncEntry) : [],
  };
}

export const meshConnectivityService = {
  async getConnectivity(signal?: AbortSignal): Promise<MeshConnectivity> {
    const res = await controlApiRequest<ConnectivityResponse | null>({
      path: '/api/admin/mesh-connectivity',
      signal,
    });

    return {
      api: toSnapshot(res?.api),
      apiError: asOptional(res?.api_error),
      worker: toSnapshot(res?.worker),
      workerError: asOptional(res?.worker_error),
    };
  },

  async listTenantMeshStatus(
    signal?: AbortSignal
  ): Promise<TenantMeshStatus[]> {
    const res = await controlApiRequest<Record<string, unknown>[] | null>({
      path: '/api/admin/tenants/mesh-status',
      signal,
    });

    if (!Array.isArray(res)) {
      return [];
    }

    return res.map((row) => ({
      tenantId: asString(row.tenant_id),
      tenantName: asString(row.tenant_name) || asString(row.tenant_id),
      registered: row.registered === true,
      meshId: asOptional(row.mesh_id),
      slug: asOptional(row.slug),
      error: asOptional(row.error),
    }));
  },

  async getUserSync(
    tenantId: string,
    signal?: AbortSignal
  ): Promise<MeshUserSyncReport> {
    const res = await controlApiRequest<unknown>({
      path: `/api/admin/tenants/${encodeURIComponent(tenantId)}/mesh-user-status`,
      signal,
    });

    return toSyncReport(res);
  },

  /** Returns a fresh report, so the caller can render the result directly. */
  async backfillUsers(tenantId: string): Promise<MeshUserSyncReport> {
    const res = await controlApiRequest<unknown>({
      method: 'POST',
      path: `/api/admin/tenants/${encodeURIComponent(tenantId)}/mesh-user-backfill`,
    });

    return toSyncReport(res);
  },
};
